<template>
  <div>
    <custom-callout
      controlled
      has-toggle
      :opened="value.opened"
      @click:open="toggle()"
    >
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="w-100">
          <div class="row d-flex align-items-center py-2 pr-3 col-12">
            <div class="col-12 col-md">
              <div class="data-name">Estatus:</div>
              <div>
                <custom-balloon
                  :background-color="value.status.color"
                  dot
                  :text="value.status.name"
                />
              </div>
            </div>

            <div class="col-12 col-md">
              <div class="data-name">Número de Batch:</div>
              <div>{{ value.number | empty }}</div>
            </div>

            <div class="col-12 col-md">
              <div class="data-name">Fecha de Creación:</div>
              <div>{{ value.date | date }}</div>
            </div>

            <div class="col-12 col-md-2 d-flex justify-content-end">
              <drop-down :items="actions" />
              <action-button
                v-if="canReview"
                class="ml-2"
                i="fas fa-flag"
                :variant="isDraft ? 'outline-success' : 'success'"
                @click="review()"
              />
            </div>

            <div class="w-100 px-3">
              <hr />
            </div>

            <div class="col-12 col-md">
              <div class="data-name">Cantidad de Reclamaciones:</div>
              <div>{{ value.count | empty }}</div>
            </div>

            <div class="col-12 col-md">
              <div class="data-name">Monto a Reclamar:</div>
              <div>{{ value.totalAmount | price }}</div>
            </div>

            <div class="col-12 col-md">
              <div class="data-name">Lugar de Servicio:</div>
              <div>{{ value.location?.name | empty }}</div>
            </div>

            <div class="col-12 col-md-2" />
          </div>
        </div>
        <div v-if="checkable">
          <check-box v-model="value.checked" />
        </div>
      </div>

      <template #edition>
        <div class="bg-secondary-3 p-2">
          <div v-if="areTabsVisible" class="bg-white p-2 c-tabs">
            <b-tabs
              v-model="currentIndexTab"
              active-nav-item-class="mipe-tab font-weight-bold text-mipe-orange mipe-active-tab"
              active-tab-class=""
              content-class="mt-3"
              nav-class="mipe-tab"
              :no-nav-style="true"
            >
              <b-tab
                :active="currentIndexTab === tabValue.History"
                title="Historial de Estatus"
                @click="onActiveTab(tabValue.History)"
              >
                <batch-history :item="value" />
              </b-tab>
              <b-tab
                :active="currentIndexTab === tabValue.Documents"
                title="Documentos"
                @click="onActiveTab(tabValue.Documents)"
              />
            </b-tabs>
          </div>
        </div>
      </template>
    </custom-callout>
    <error
      v-if="canSendFile"
      class="mt-0"
      is-alert-mode
      message="Batch procesado; falta enviar el archivo por SFTP, ya que ocurrió un error durante el proceso de envío."
    />
  </div>
</template>

<script>
//mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

//utils
import BatchStatus, {
  names as BatchStatusNames,
} from '@/utils/constants/medicaidBatchStatus';
import moment from 'moment';

//services
import {
  changeBatchStatus,
  sendFile,
} from '@/modules/medicaid/medicaid-claim.api';
import appServices from '@/modules/app/api.js';

//components
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import CustomBalloon from 'custom-balloon';
import Error from 'custom-error';
import ActionButton from 'ActionButton';
import CheckBox from 'CheckBox';

//local components
import BatchHistory from './BatchHistory.vue';

//constants
const TABS = Object.freeze({
  History: 0,
  Documents: 1,
});

import ClaimsTabs from '@/utils/constants/claimsTabs';

export default {
  name: 'BatchResultRow',
  components: {
    CustomCallout,
    DropDown,
    CustomBalloon,
    BatchHistory,
    Error,
    ActionButton,
    CheckBox,
  },
  filters: {
    time(value) {
      return value ? moment(value, 'HH:mm:ss').format('hh:mm A') : '-';
    },
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    checkable: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      currentIndexTab: null,
    };
  },
  computed: {
    tabValue() {
      return TABS;
    },
    areTabsVisible() {
      return [TABS.History, TABS.Documents].includes(this.currentIndexTab);
    },
    isCancellable() {
      return (
        this.value.status.id === BatchStatus.Draft &&
        this.userHasPermissions('provider.medicaid.claims.batch.cancel')
      );
    },
    canSendFile() {
      return (
        this.value.status.id === BatchStatus.Submitted &&
        this.value.fileSent === false
      );
    },
    canDownloadDocument() {
      return (
        this.value.status.id === BatchStatus.Submitted && this.value.documentId
      );
    },
    actions() {
      return [
        {
          name: 'enter',
          label: 'Entrar',
          icon: 'fas fa-file-import text-green',
          click: () => this.goToDetail(),
        },
        {
          name: 'cancel',
          label: 'Cancelar',
          visible: this.isCancellable,
          icon: 'fas fa-times-circle text-danger',
          click: () => this.changeStatus(BatchStatus.Cancelled),
        },
        {
          name: 'sendFile',
          label: 'Enviar Archivo',
          visible: this.canSendFile,
          icon: 'fas fa-file-upload text-green',
          click: () => this.sendFile(),
        },
        {
          name: 'downloadDocument',
          label: 'Descargar Documento',
          visible: this.canDownloadDocument,
          icon: 'fas fa-file-download text-green',
          click: () => this.downloadDocument(),
        },
        {
          name: 'history',
          label: 'Historial de Estatus',
          icon: 'fas fa-history text-green',
          click: () => {
            this.openTabs(TABS.History);
          },
        },
      ];
    },
    isDraft() {
      return this.value.status.id === BatchStatus.Draft;
    },
    isReviewed() {
      return this.value.status.id === BatchStatus.Reviewed;
    },
    canReview() {
      return this.isDraft || this.isReviewed;
    },
  },
  methods: {
    openTabs(tabIndex) {
      this.currentIndexTab = Number(tabIndex);
      this.value.opened = true;
    },
    toggle() {
      const willClose = this.value.opened === true;

      this.value.opened = !this.value.opened;

      if (willClose) {
        this.currentIndexTab = null;
      }
    },
    onActiveTab(indexTab) {
      this.currentIndexTab = Number(indexTab);
    },
    cancel() {
      this.currentIndexTab = null;
      this.value.opened = false;
    },
    async changeStatus(status) {
      const batchNumber = this.value?.number ?? '';
      const statusName = BatchStatusNames[status] ?? '';
      const message = {
        confirmation: `Esta acción marcará el batch <b>${batchNumber}</b> como <b>${statusName}</b>. <div class="mt-2">¿Desea continuar?</div>`,
        success: `El batch ${batchNumber} fue marcado como ${statusName} exitosamente.`,
        error: `No fue posible marcar el batch ${batchNumber} a ${statusName}. Intente nuevamente o contacte al administrador.`,
      };

      const confirmation = await this.ShowCancelModal(
        'Confirmación',
        message.confirmation,
        'Si',
        'No'
      );

      if (!confirmation) return;

      try {
        await changeBatchStatus(this.value.id, { status });
        this.$emit('refresh');
        this.ShowToast('Éxito', message.success, 'success');
      } catch (error) {
        this.ShowToast('Atención', message.error, 'error');
      }
    },
    goToDetail() {
      this.$router.push({
        name: 'medicaid-claims-batch',
        params: { id: this.value.id },
        query: { tab: ClaimsTabs.Reclamaciones },
      });
    },
    downloadDocument() {
      return appServices.downloadDocumentByAttachmentId(this.value.documentId);
    },
    async sendFile() {
      if (!this.canSendFile) return;

      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea enviar el archivo por SFTP?',
        'Sí',
        'No'
      );
      if (!confirmed) return;

      const message = {
        success: 'Archivo enviado exitosamente',
        error:
          'No fue posible enviar el archivo. Intente nuevamente o contacte al administrador.',
      };

      try {
        const response = await sendFile(this.value.id);
        const result = response?.data ?? response;

        if (result === true) {
          this.$emit('refresh');
          this.ShowToast('Éxito', message.success, 'success');
        } else {
          this.ShowToast('Error', message.error, 'error');
        }
      } catch (error) {
        this.ShowToast('Atención', error?.message || message.error, 'error');
      }
    },
    async review() {
      if (!this.canReview) return;
      const status = this.isDraft ? BatchStatus.Reviewed : BatchStatus.Draft;
      await this.changeStatus(status);
    },
  },
};
</script>

<style scoped lang="scss">
.bg-green-gray {
  background-color: #ebf1ee !important;
}
.c-tabs {
  ::v-deep .nav-link.active {
    color: #2db782;
    padding-bottom: 5px;
    border-bottom: 3px #2db782 solid !important;
  }
  ::v-deep ul.mipe-tab {
    display: flex;
    width: 100%;
  }

  ::v-deep ul.mipe-tab .nav-item {
    flex: 1;
    text-align: center;
  }
}
</style>
